import { useState } from "react";

import type { Meta, StoryObj } from "@storybook/react";

import type {
  ConfirmationAlertProps,
  ConfirmationAlertType,
} from "@/ui/composed/alerts/ConfirmationAlert";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { Button, FAIconName } from "@/ui/core";
import { joinTestIDs } from "@/utils/string.util";

const ConfirmationAlertTypeValues: Record<
  ConfirmationAlertType,
  ConfirmationAlertType
> = {
  destructive: "destructive",
  neutral: "neutral",
};

const testID = joinTestIDs("storybook", "confirmation-alert");
const StoryMeta: Meta<ConfirmationAlertProps> = {
  title: "Components/Alerts/Confirmation Alert",
  component: ConfirmationAlert,
  argTypes: {
    confirmationType: {
      control: "select",
      options: Object.values(ConfirmationAlertTypeValues),
    },
    confirmLeftIcon: { control: "select", options: Object.values(FAIconName) },
    confirmRightIcon: { control: "select", options: Object.values(FAIconName) },
    confirmText: { control: "text" },
    message: { control: "text" },
    onConfirm: { action: "confirm" },
    onDismiss: { action: "dismiss" },
    title: { control: "text" },
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const handleDismiss = () => {
      args.onDismiss();
      setIsOpen(false);
    };
    return (
      <>
        <Button
          onPress={() => setIsOpen(true)}
          testID={testID}
          title="Open Alert"
          variant={
            args.confirmationType === "destructive"
              ? "accent.critical"
              : "primary"
          }
        />
        <ConfirmationAlert
          {...args}
          isOpen={isOpen}
          onDismiss={handleDismiss}
          testID={testID}
        />
      </>
    );
  },
};

type Story = StoryObj<ConfirmationAlertProps>;

const Destructive: Story = {
  args: {
    confirmationType: "destructive",
    confirmLeftIcon: FAIconName.UserSlash,
    confirmText: "Unregister from this group",
    message:
      "Once you unregister from this group, you may not be able to register again.",
    title: "Are you sure?",
  },
};

const Neutral: Story = {
  args: {
    confirmationType: "neutral",
    confirmLeftIcon: FAIconName.CalendarCirclePlus,
    confirmText: "Add to calendar",
    message:
      "You can view all upcoming appointments in your Schedule, or add to your personal calendar below.",
    title: "Appointment scheduled",
  },
};

export default StoryMeta;

export { Destructive, Neutral };
